import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import LoginForm from "@/components/auth/LoginForm";
import { loginFailure, loginStart, loginSuccess } from "@/redux/auth/authSlice";
import { authService } from "@/services/authService";
import { RootState } from "@/store";
import { LoginCredentials } from "@/types/auth";

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error, isLoading } = useSelector((state: RootState) => state.auth);

  const handleLogin = async (credentials: LoginCredentials) => {
    try {
      dispatch(loginStart());

      // Use real API authentication service
      const response = await authService.login(credentials);

      if (response.success && response.token && response.user) {
        // Debug: Log detailed login information
        console.log("🔍 LOGIN DEBUG - Login successful:");
        console.log("- User:", response.user);
        console.log("- Role:", response.user.role);
        console.log("- Email:", response.user.email);
        console.log("- Token:", response.token.substring(0, 20) + "...");

        dispatch(loginSuccess({ token: response.token, user: response.user }));

        // Debug: log user role for troubleshooting
        console.log("User role after dispatch:", response.user.role);

        // Redirect based on user role
        if (
          [
            "delivery",
            "DeliveryDriver",
            "driver",
            "scooter",
            "ScooterDriver",
            "VehicleDriver",
          ].includes(response.user.role)
        ) {
          console.log("Redirecting to driver dashboard");
          navigate("/driver/dashboard", { replace: true });
        } else if (["admin", "manager", "staff"].includes(response.user.role)) {
          console.log("Redirecting admin/manager/staff to home");
          navigate("/", { replace: true }); // Redirect to home page until general dashboard is created
        } else {
          console.log(
            "Redirecting other roles to home, role was:",
            response.user.role,
          );
          navigate("/", { replace: true }); // Redirect to home page for other roles
        }
      } else {
        // API returned success: false or missing data
        dispatch(loginFailure(response.message || "Login failed"));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      console.error("Login error:", err);
      dispatch(loginFailure(message));
    }
  };

  const handleGoogleLogin = async () => {
    try {
      dispatch(loginStart());

      // TODO: Implement Google OAuth
      // For now, simulate Google login
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const mockUser = {
        createdAt: new Date().toISOString(),
        email: "user@gmail.com",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        emergency_contact_relationship: "",
        emergency_contact_surname: "",
        firstName: "Jane",
        full_name: "Jane Smith",
        id: "2",
        isEmailVerified: true,
        lastLoginAt: new Date().toISOString(),
        lastName: "Smith",
        name: "Jane Smith",
        role: "manager" as const,
      };

      const mockToken = "mock-google-jwt-token-" + Date.now();

      dispatch(loginSuccess({ token: mockToken, user: mockUser }));
      navigate("/", { replace: true }); // Redirect to home page
    } catch (err) {
      dispatch(loginFailure("Google login failed"));
    }
  };

  return (
    <LoginForm
      error={error || undefined}
      isLoading={isLoading}
      onGoogleLogin={handleGoogleLogin}
      onSubmit={handleLogin}
    />
  );
}
